'use client';

import { tgMetricsAtom } from '@/atoms/tgSentiment';
import { useAtomValue } from 'jotai';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { LucideIcon, MessageSquare, Shield, UserCircle, Users } from 'lucide-react';
import { LineChart } from './components/LineChart';
import { cn } from '@/lib/utils';

export default function TGMetrics() {
  const data = useAtomValue(tgMetricsAtom);

  if (!data) return null;

  const averageSentiment =
    data.sentiment.reduce((acc, curr) => acc + curr.score, 0) / data.sentiment.length;
  const avgMessagesPerDay = Math.round(
    data.total_messages.reduce((acc, curr) => acc + curr.count, 0) / data.total_messages.length,
  );
  const avgActiveUsersPerDay = Math.round(
    data.active_users.reduce((acc, curr) => acc + curr.count, 0) / data.active_users.length,
  );

  const scoreFormat = (score: number) => {
    if (score < 3) return { text: 'text-red-500', bg: 'bg-red-500/20', subtitle: 'Bad' };
    if (score < 7) return { text: 'text-yellow-500', bg: 'bg-yellow-500/20', subtitle: 'Ok' };
    return { text: 'text-emerald-500', bg: 'bg-emerald-500/20', subtitle: 'Great' };
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Telegram Analysis</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center space-x-4">
            <div
              className={cn(
                'rounded-full aspect-square p-4 flex items-center justify-center',
                scoreFormat(averageSentiment).bg,
              )}
            >
              <div
                className={cn(
                  'text-2xl leading-none font-bold',
                  scoreFormat(averageSentiment).text,
                )}
              >
                {Number(averageSentiment * 10).toFixed(0)}
              </div>
            </div>
            <div>
              <h2 className="text-xl text-gray-100">Analysis Score</h2>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <StatItem icon={Users} label="Members" value={data.member_count} />
            <StatItem icon={Shield} label="Moderators" value={data.moderator_count} />
            <StatItem icon={MessageSquare} label="Avg Messages/Day" value={avgMessagesPerDay} />
            <StatItem icon={UserCircle} label="Avg Active Users" value={avgActiveUsersPerDay} />
          </div>

          <MetricCard
            title="Sentiment Score"
            value={averageSentiment.toFixed(2)}
            data={data.sentiment}
            dataKey="score"
            valueColor={scoreFormat(averageSentiment).text}
            subtitle={scoreFormat(averageSentiment).subtitle}
          />

          <MetricCard
            title="Messages per Day"
            value={data.total_messages[data.total_messages.length - 1].count}
            data={data.total_messages}
            dataKey="count"
          />

          <MetricCard
            title="Active Users"
            value={data.active_users[data.active_users.length - 1].count}
            data={data.active_users}
            dataKey="count"
          />

          <MetricCard
            title="Active Moderators"
            value={data.active_moderators[data.active_moderators.length - 1].count}
            data={data.active_moderators}
            dataKey="count"
          />
        </div>
      </CardContent>
      <CardFooter>
        <p className="text-right text-muted-foreground text-sm ml-auto">
          powered by{' '}
          <a
            className="text-white hover:text-purple-400"
            href="https://www.tgmetrics.ai/"
            target="_blank"
          >
            TgMetrics
          </a>
        </p>
      </CardFooter>
    </Card>
  );
}

interface StatItemProps {
  icon: LucideIcon;
  label: string;
  value: string | number;
}

function StatItem({ icon: Icon, label, value }: StatItemProps) {
  return (
    <div className="flex items-center space-x-2">
      <Icon className="text-gray-400" size={20} />
      <div>
        <div className="text-sm text-gray-400">{label}</div>
        <div className="font-semibold text-gray-100">{value}</div>
      </div>
    </div>
  );
}

interface MetricCardProps {
  title: string;
  value: string | number;
  data: any[];
  dataKey: string;
  valueColor?: string;
  subtitle?: string;
}

export function MetricCard({
  title,
  value,
  data,
  dataKey,
  valueColor = 'text-gray-100',
  subtitle,
}: MetricCardProps) {
  return (
    <div className="p-6 bg-gray-800/50 rounded-lg">
      <h3 className="text-lg font-semibold text-gray-100 mb-2">{title}</h3>
      <div className="flex items-baseline space-x-2 mb-4">
        <div className={`text-3xl font-bold ${valueColor}`}>{value}</div>
        {subtitle && <div className={valueColor}>{subtitle}</div>}
      </div>
      <div className="bg-gray-800/50 rounded-lg p-4">
        <LineChart data={data} dataKey={dataKey} />
      </div>
    </div>
  );
}
